import {
  CheckboxCheckedIcon,
  CloseIcon,
  DeselectIcon,
  MoreIcon,
  SelectAllIcon,
  SyncIcon,
} from '../../../components/ui/icons';
import { useState } from 'react';
import { visuallyHidden } from '@mui/utils';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import {
  CollectionBulkAction,
  CollectionMenuAction,
  CollectionSelectionController,
} from '../types';

const interactiveToolbarIconStyles = {
  '& .MuiButton-root:not(.Mui-disabled):not(.MuiButton-colorError) .MuiSvgIcon-root':
    { color: 'text.secondary' },
  '& .MuiIconButton-root:not(.Mui-disabled) .MuiSvgIcon-root': {
    color: 'text.secondary',
  },
  '& .MuiToggleButton-root:not(.Mui-disabled) .MuiSvgIcon-root': {
    color: 'text.secondary',
  },
  '& .MuiToggleButton-root.Mui-selected:not(.Mui-disabled) .MuiSvgIcon-root': {
    color: 'primary.main',
  },
} as const;

export function CollectionHeader({
  noun,
  hideCount = false,
  onRefresh,
  resultCount,
  incomplete = false,
  inventoryStatus,
  totalCount,
  visibleIds,
  selection,
  phoneLayout,
  filterControl,
  sortControl,
  viewControl,
  expandedPanel,
  bulkActions,
  extraMenuActions = [],
  pageMenuActions = [],
  disableSelection = false,
}: {
  noun: string;
  hideCount?: boolean;
  onRefresh?: () => void;
  resultCount: number;
  incomplete?: boolean;
  inventoryStatus?: 'loading' | 'updating';
  totalCount: number;
  visibleIds: string[];
  selection: CollectionSelectionController;
  phoneLayout: boolean;
  filterControl?: React.ReactNode;
  sortControl: React.ReactNode;
  viewControl: React.ReactNode;
  expandedPanel?: React.ReactNode;
  bulkActions: CollectionBulkAction[];
  extraMenuActions?: CollectionMenuAction[];
  pageMenuActions?: CollectionMenuAction[];
  disableSelection?: boolean;
}) {
  const [selectionAnchor, setSelectionAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [pageActionsAnchor, setPageActionsAnchor] =
    useState<HTMLElement | null>(null);
  const updating = inventoryStatus === 'updating';
  const allResultsSelected =
    visibleIds.length > 0 && visibleIds.every(selection.isSelected);
  const label = totalCount === 1 ? noun : `${noun}s`;
  const countLabel = `${resultCount} of ${totalCount}${incomplete ? ' loaded' : ''} ${label}`;
  const visibleCountLabel = phoneLayout
    ? `${resultCount === totalCount ? totalCount : `${resultCount} / ${totalCount}`}${incomplete ? ' loaded' : ''} ${label}`
    : countLabel;

  const closeSelectionMenu = () => setSelectionAnchor(null);
  const runSelectionAction = (action: () => void) => {
    action();
    closeSelectionMenu();
  };
  const selectionMenu = (
    <Menu
      id={`${noun}-selection-menu`}
      anchorEl={selectionAnchor}
      open={Boolean(selectionAnchor)}
      onClose={closeSelectionMenu}
    >
      {!selection.active && (
        <MenuItem
          onClick={() => runSelectionAction(selection.enter)}
          disabled={!resultCount}
        >
          <ListItemIcon>
            <CheckboxCheckedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{`Select ${noun}s`}</ListItemText>
        </MenuItem>
      )}
      <MenuItem
        onClick={() =>
          runSelectionAction(() => selection.toggleVisible(visibleIds))
        }
        disabled={!resultCount}
      >
        <ListItemIcon>
          {allResultsSelected ? (
            <DeselectIcon fontSize="small" />
          ) : (
            <SelectAllIcon fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText>
          {allResultsSelected ? 'Deselect all results' : 'Select all results'}
        </ListItemText>
      </MenuItem>
      {selection.active && (
        <MenuItem
          onClick={() => runSelectionAction(selection.clear)}
          disabled={!selection.selectedIds.size}
        >
          <ListItemIcon>
            <DeselectIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Deselect all</ListItemText>
        </MenuItem>
      )}
      {extraMenuActions.map((action) => (
        <MenuItem
          key={action.label}
          onClick={() => runSelectionAction(action.onClick)}
          disabled={action.disabled}
        >
          <ListItemIcon>{action.icon}</ListItemIcon>
          <ListItemText>{action.label}</ListItemText>
        </MenuItem>
      ))}
    </Menu>
  );

  const selectionButton = disableSelection ? null : (
    <IconButton
      size="small"
      aria-label={selection.active ? 'Selection' : 'Select'}
      title={selection.active ? 'Selection options' : `Select ${noun}s`}
      aria-controls={selectionAnchor ? `${noun}-selection-menu` : undefined}
      aria-haspopup="menu"
      aria-expanded={selectionAnchor ? 'true' : undefined}
      onClick={(event) => setSelectionAnchor(event.currentTarget)}
      disabled={!totalCount}
      sx={{ flexShrink: 0 }}
    >
      <CheckboxCheckedIcon />
    </IconButton>
  );

  const pageActionsButton =
    pageMenuActions.length > 0 ? (
      <>
        <IconButton
          size="small"
          aria-label="More page actions"
          aria-haspopup="menu"
          aria-expanded={pageActionsAnchor ? 'true' : undefined}
          onClick={(event) => setPageActionsAnchor(event.currentTarget)}
          sx={{ flexShrink: 0 }}
        >
          <MoreIcon />
        </IconButton>
        <Menu
          id={`${noun}-page-actions-menu`}
          anchorEl={pageActionsAnchor}
          open={Boolean(pageActionsAnchor)}
          onClose={() => setPageActionsAnchor(null)}
        >
          {pageMenuActions.map((action) => (
            <MenuItem
              key={action.label}
              onClick={() => {
                action.onClick();
                setPageActionsAnchor(null);
              }}
              disabled={action.disabled}
              sx={
                action.color === 'error' ? { color: 'error.main' } : undefined
              }
            >
              <ListItemIcon
                sx={
                  action.color === 'error' ? { color: 'error.main' } : undefined
                }
              >
                {action.icon}
              </ListItemIcon>
              <ListItemText>{action.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </>
    ) : null;

  const bulkActionControls = phoneLayout ? (
    <>
      <IconButton
        size="small"
        aria-label="Actions"
        aria-controls={actionsAnchor ? `${noun}-bulk-actions-menu` : undefined}
        aria-haspopup="menu"
        aria-expanded={actionsAnchor ? 'true' : undefined}
        onClick={(event) => setActionsAnchor(event.currentTarget)}
        disabled={!bulkActions.length}
      >
        <MoreIcon />
      </IconButton>
      <Menu
        id={`${noun}-bulk-actions-menu`}
        anchorEl={actionsAnchor}
        open={Boolean(actionsAnchor)}
        onClose={() => setActionsAnchor(null)}
      >
        {bulkActions.map((action) => (
          <MenuItem
            key={action.label}
            onClick={() => {
              action.onClick();
              setActionsAnchor(null);
            }}
            disabled={action.disabled}
            sx={action.color === 'error' ? { color: 'error.main' } : undefined}
          >
            <ListItemIcon
              sx={
                action.color === 'error' ? { color: 'error.main' } : undefined
              }
            >
              {action.icon}
            </ListItemIcon>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  ) : (
    bulkActions.map((action) => (
      <Button
        key={action.label}
        size="small"
        startIcon={action.icon}
        color={action.color}
        onClick={action.onClick}
        disabled={action.disabled}
        sx={{ flexShrink: 0 }}
      >
        {action.label}
      </Button>
    ))
  );

  return (
    <Box data-testid="collection-header" sx={{ minWidth: 0 }}>
      {selection.active ? (
        <Paper
          variant="outlined"
          sx={{
            minHeight: 56,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexWrap: phoneLayout ? 'wrap' : 'nowrap',
            overflowX: phoneLayout ? 'clip' : 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            borderColor: 'primary.main',
            borderRadius: 1,
            bgcolor: 'action.selected',
            ...interactiveToolbarIconStyles,
          }}
        >
          <IconButton
            size="small"
            aria-label="Clear selection"
            onClick={selection.clear}
            sx={{ flexShrink: 0 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            fontWeight={600}
            sx={{
              mr: 'auto',
              flexShrink: phoneLayout ? 1 : 0,
              minWidth: 0,
              whiteSpace: 'nowrap',
              fontSize: phoneLayout
                ? 'clamp(0.7rem, 3.2vw, 0.875rem)'
                : undefined,
            }}
            aria-live="polite"
          >
            {selection.selectedIds.size} selected
          </Typography>
          {bulkActionControls}
          {selectionButton}
          {selectionMenu}
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              minHeight: 48,
              display: 'flex',
              flexWrap: phoneLayout ? 'wrap' : 'nowrap',
              alignItems: 'center',
              gap: phoneLayout ? 1 : 2,
              // Keep shadows and focus rings inside the scroll viewport.
              // Horizontal scrolling also clips vertical overflow in CSS.
              p: 1.5,
              overflowX: phoneLayout ? 'clip' : 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              ...interactiveToolbarIconStyles,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: phoneLayout ? 1 : 2,
                mr: 'auto',
                flexShrink: 0,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  ...(hideCount ? visuallyHidden : {}),
                  mr: 'auto',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  fontSize: phoneLayout
                    ? 'clamp(0.7rem, 3.2vw, 0.875rem)'
                    : undefined,
                }}
                aria-live="polite"
                aria-label={countLabel}
                title={countLabel}
              >
                {visibleCountLabel}
              </Typography>
              {inventoryStatus === 'loading' && (
                <Chip
                  size="small"
                  role="status"
                  label="Loading…"
                  title="Loading remaining entries. Results may be incomplete."
                  sx={{
                    height: 20,
                    fontSize: '0.6875rem',
                    color: 'text.secondary',
                    bgcolor: 'action.hover',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )}
              <Box role="status" sx={visuallyHidden}>
                {updating ? `Updating ${noun}s` : ''}
              </Box>
              {onRefresh && (
                <IconButton
                  size="small"
                  aria-label={updating ? `Updating ${noun}s` : 'Refresh'}
                  title={updating ? `Updating ${noun}s` : 'Refresh'}
                  onClick={onRefresh}
                  disabled={updating}
                >
                  <SyncIcon
                    sx={{
                      animation: updating
                        ? 'collection-refresh-spin 1s linear infinite'
                        : 'none',
                      '@keyframes collection-refresh-spin': {
                        to: { transform: 'rotate(360deg)' },
                      },
                      '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none',
                      },
                    }}
                  />
                </IconButton>
              )}
            </Box>
            {filterControl}
            {sortControl}
            {viewControl}
            {pageActionsButton}
            {/* Center the 1px divider within one toolbar gap. */}
            {!disableSelection && (
              <Divider
                orientation="vertical"
                flexItem
                sx={{ my: 1.25, mx: phoneLayout ? '-4.5px' : '-8.5px' }}
              />
            )}
            {selectionButton}
            {selectionMenu}
          </Box>
          {expandedPanel}
        </>
      )}
    </Box>
  );
}
